import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Indicator,
  Menu,
  Text,
} from '@mantine/core';
import {BasicButton} from '@packages/ui';
import classNames from 'classnames';
import type React from 'react';
import {useEffect, useState} from 'react';
import {MdExpandLess, MdFilterAlt, MdUnfoldMore} from 'react-icons/md';
import type {z} from 'zod';
import type {CompanyDepartmentMasterSchema} from '../../../../../core/entities/reports/schema';
import type {
  FilterItem,
  ReportTypeValue,
} from '../../../usecase/useHandleStudy';

type Department = z.infer<typeof CompanyDepartmentMasterSchema>;

type FilterProps = {
  filterItems: FilterItem;
  departments: Department[];
  graduationYears: number[];
  reportTypes: ReportTypeValue[];
  handleChangeOrder: (value: 'recent' | 'popular') => void;
  handleChangeReportType: (value: string[]) => void;
  handleChangeDepartment: (value: string[]) => void;
  handleChangeGradYear: (value: string[]) => void;
};

export const StudyFilter: React.FC<FilterProps> = ({
  filterItems,
  departments,
  graduationYears,
  reportTypes,
  handleChangeOrder,
  handleChangeReportType,
  handleChangeDepartment,
  handleChangeGradYear,
}) => {
  return (
    <>
      <div className="hidden pc:grid">
        <PcFilter
          filterItems={filterItems}
          departments={departments}
          graduationYears={graduationYears}
          reportTypes={reportTypes}
          handleChangeOrder={handleChangeOrder}
          handleChangeReportType={handleChangeReportType}
          handleChangeDepartment={handleChangeDepartment}
          handleChangeGradYear={handleChangeGradYear}
        />
      </div>
      <div className="pc:hidden">
        <MobileFilter
          filterItems={filterItems}
          departments={departments}
          graduationYears={graduationYears}
          reportTypes={reportTypes}
          handleChangeOrder={handleChangeOrder}
          handleChangeReportType={handleChangeReportType}
          handleChangeDepartment={handleChangeDepartment}
          handleChangeGradYear={handleChangeGradYear}
        />
      </div>
    </>
  );
};

const PcFilter: React.FC<FilterProps> = ({
  filterItems,
  departments,
  graduationYears,
  reportTypes,
  handleChangeOrder,
  handleChangeReportType,
  handleChangeDepartment,
  handleChangeGradYear,
}) => {
  return (
    <Box
      className={classNames(
        'flex justify-between items-center bg-white px-16 py-8 shadow-card mx-0 mt-16 rounded-b-[20px] rounded-[20px]',
      )}
    >
      <div className="flex gap-12 my-4">
        <Menu>
          <Menu.Target>
            <Button
              className={classNames(
                'rounded-[20px] border-none bg-background text-primary',
              )}
              variant="outline"
            >
              <div className="flex gap-8">
                {filterItems.reportType.length > 0 ? (
                  <Indicator size={6} color="red">
                    <MdFilterAlt size={20} />
                  </Indicator>
                ) : (
                  <MdFilterAlt size={20} />
                )}
                <span className="p-4">レポートの種類</span>
                <MdUnfoldMore size={20} />
              </div>
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Checkbox.Group
              value={filterItems.reportType.map(String)}
              onChange={handleChangeReportType}
            >
              {reportTypes.map((type) => (
                <Checkbox
                  key={type.key}
                  className="p-4"
                  styles={{
                    labelWrapper: {
                      width: '100%',
                      userSelect: 'none',
                    },
                  }}
                  value={String(type.key)}
                  label={<span className="w-full mx-auto">{type.name}</span>}
                />
              ))}
            </Checkbox.Group>
          </Menu.Dropdown>
        </Menu>

        <Menu>
          <Menu.Target>
            <Button
              className="rounded-[20px] border-none bg-background text-primary"
              variant="outline"
            >
              <div className="flex gap-8">
                {filterItems.department.length > 0 ? (
                  <Indicator size={6} color="red">
                    <MdFilterAlt size={20} />
                  </Indicator>
                ) : (
                  <MdFilterAlt size={20} />
                )}
                <span className="p-4">部門・職種</span>
                <MdUnfoldMore size={20} />
              </div>
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Checkbox.Group
              value={filterItems.department.map(String)}
              onChange={handleChangeDepartment}
            >
              {departments.map((department) => (
                <Checkbox
                  key={department.id}
                  className="p-4"
                  styles={{
                    labelWrapper: {
                      width: '100%',
                      userSelect: 'none',
                    },
                  }}
                  value={String(department.id)}
                  checked={departments
                    .map((dep) => dep.id)
                    .includes(department.id)}
                  label={department.name}
                />
              ))}
            </Checkbox.Group>
          </Menu.Dropdown>
        </Menu>

        <Menu>
          <Menu.Target>
            <Button
              className="rounded-[20px] border-none bg-background text-primary"
              variant="outline"
            >
              <div className={classNames('flex pc:gap-8')}>
                {filterItems.graduationYear.length > 0 ? (
                  <Indicator size={6} color="red">
                    <MdFilterAlt size={20} />
                  </Indicator>
                ) : (
                  <MdFilterAlt size={20} />
                )}
                <span className="p-4">卒年</span>
                <MdUnfoldMore size={20} />
              </div>
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Checkbox.Group
              value={filterItems.graduationYear.map(String)}
              onChange={handleChangeGradYear}
            >
              {graduationYears.map((year) => (
                <Checkbox
                  key={year}
                  className="p-4"
                  styles={{
                    labelWrapper: {
                      width: '100%',
                      userSelect: 'none',
                    },
                  }}
                  checked={filterItems.graduationYear.includes(year)}
                  value={String(year)}
                  label={`${String(year).slice(2, 4)}卒`}
                />
              ))}
            </Checkbox.Group>
          </Menu.Dropdown>
        </Menu>
      </div>

      <select
        value={filterItems.sortOrder}
        onChange={(e) => {
          handleChangeOrder(e.target.value as 'recent' | 'popular');
        }}
        className="font-bold"
      >
        <option value="recent">新着順</option>
        <option value="popular">クリップ数順</option>
      </select>
    </Box>
  );
};

const MobileFilter: React.FC<FilterProps> = ({
  filterItems,
  departments,
  graduationYears,
  reportTypes,
  handleChangeOrder,
  handleChangeReportType,
  handleChangeDepartment,
  handleChangeGradYear,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedReportTypes, setSelectedReportTypes] = useState<string[]>(
    filterItems.reportType.map(String),
  );
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>(
    filterItems.department.map(String),
  );
  const [selectedGraduationYears, setSelectedGraduationYears] = useState<
    string[]
  >(filterItems.graduationYear.map(String));

  useEffect(() => {
    setSelectedReportTypes(filterItems.reportType.map(String));
    setSelectedDepartments(filterItems.department.map(String));
    setSelectedGraduationYears(filterItems.graduationYear.map(String));
  }, [filterItems]);

  const handleFilterButtonClick = () => {
    handleChangeReportType(selectedReportTypes);
    handleChangeDepartment(selectedDepartments);
    handleChangeGradYear(selectedGraduationYears);
    setIsDrawerOpen(false);
  };

  return (
    <>
      <Box className="flex items-center justify-between bg-white px-16 py-8 shadow-card mx-16 rounded-b-[20px]">
        <Box
          className="flex items-center"
          onClick={() => {
            setIsDrawerOpen(!isDrawerOpen);
          }}
        >
          {isDrawerOpen ? (
            <>
              <MdExpandLess size="20px" />
              <Text className="font-bold ml-4 text-base">閉じる</Text>
            </>
          ) : selectedDepartments.length > 0 ||
            selectedGraduationYears.length > 0 ||
            selectedReportTypes.length > 0 ? (
            <>
              <Indicator size={6} color="red">
                <MdFilterAlt size="16px" />
              </Indicator>
              <Text className="font-bold ml-4 text-base">絞り込む</Text>
              <Box className="font-bold text-sm text-white bg-black rounded-full text-center px-4 ml-4">
                {selectedDepartments.length +
                  selectedGraduationYears.length +
                  selectedReportTypes.length >
                9
                  ? '9+'
                  : selectedDepartments.length +
                    selectedGraduationYears.length +
                    selectedReportTypes.length}
              </Box>
            </>
          ) : (
            <>
              <MdFilterAlt size="16px" />
              <Text className="font-bold ml-4 text-base">絞り込む</Text>
            </>
          )}
        </Box>
        <Box className="flex text-sm items-center">
          <select
            value={filterItems.sortOrder}
            onChange={(e) => {
              handleChangeOrder(e.target.value as 'recent' | 'popular');
            }}
            className="font-bold bg-white appearance-none text-base"
            style={{direction: 'rtl'}}
          >
            <option value="recent">新着順</option>
            <option value="popular">クリップ数順</option>
          </select>
          <MdUnfoldMore size={16} className="ml-4" />
        </Box>
      </Box>
      <Collapse in={isDrawerOpen}>
        <Box className="flex flex-col bg-white px-16 py-8 shadow-card mx-16 mt-8 rounded-[20px] gap-8">
          <BasicButton
            type="button"
            className="mt-12 max-w-[600px]"
            onClick={handleFilterButtonClick}
          >
            絞り込む
          </BasicButton>
          <Box>
            <Text className="font-bold text-base">レポートの種類</Text>
            <Checkbox.Group
              value={selectedReportTypes}
              onChange={setSelectedReportTypes}
            >
              {reportTypes.map((type) => (
                <Checkbox
                  key={type.key}
                  className="p-4"
                  styles={{
                    labelWrapper: {
                      width: '100%',
                      userSelect: 'none',
                    },
                  }}
                  value={String(type.key)}
                  label={<span className="w-full mx-auto">{type.name}</span>}
                />
              ))}
            </Checkbox.Group>
          </Box>
          <Box>
            <Text className="font-bold text-base">部門・職種</Text>
            <Checkbox.Group
              value={selectedDepartments}
              onChange={setSelectedDepartments}
            >
              {departments.map((department) => (
                <Checkbox
                  key={department.id}
                  className="p-4"
                  styles={{
                    labelWrapper: {
                      width: '100%',
                      userSelect: 'none',
                    },
                  }}
                  value={String(department.id)}
                  checked={departments
                    .map((dep) => dep.id)
                    .includes(department.id)}
                  label={department.name}
                />
              ))}
            </Checkbox.Group>
          </Box>
          <Box>
            <Text className="font-bold text-base">卒年</Text>
            <Checkbox.Group
              value={selectedGraduationYears}
              onChange={setSelectedGraduationYears}
            >
              {graduationYears.map((year) => (
                <Checkbox
                  key={year}
                  className="p-4"
                  styles={{
                    labelWrapper: {
                      width: '100%',
                      userSelect: 'none',
                    },
                  }}
                  checked={selectedGraduationYears.includes(String(year))}
                  value={String(year)}
                  label={`${String(year).slice(2, 4)}卒`}
                />
              ))}
            </Checkbox.Group>
          </Box>
          <BasicButton
            children="絞り込む"
            type="button"
            className="mb-8 w-full"
            onClick={handleFilterButtonClick}
          />
        </Box>
      </Collapse>
    </>
  );
};
